import React from 'react';
import { ActionButton, Flex, Text } from '@adobe/react-spectrum';
import LinkOut from '@spectrum-icons/workflow/LinkOut';
import { CardTypeIcon } from './CardTypeIcon';
import { getPrettyCategoryLabel } from '../../../data/CategoryPrettyDisplayUtils';
import './cardStats.css';
import './deckPaneCardRow.css';

/**
 * Per-row renderer for the deck panes. Regular card items use the shared
 * `.cardStatRow` treatment; combo items (`item.type === 'combo'`) render
 * with the same `.comboRow` layout as the Commander combos tab so both
 * surfaces look identical.
 */
export function DeckPaneCardRow({
    item,
    cards,
    showScore,
    showLinkOut,
    onSelectCard,
    onOpenComboDetails,
}) {
    if (item?.type === 'combo') {
        return (
            <DeckPaneComboRow
                combo={item}
                showScore={showScore}
                showLinkOut={showLinkOut}
                onSelectCard={onSelectCard}
                onOpenComboDetails={onOpenComboDetails}
            />
        );
    }

    const cardData = cards?.[item.id] || item || {};
    const imageUri = cardData.imageUri || item?.imageUri;
    const types = cardData.types || item?.types;

    const handleSelect = () => {
        if (imageUri && onSelectCard) onSelectCard(cardData);
    };

    return (
        <div className="cardStatRow deckPaneCardRow">
            <Flex direction="row" alignItems="center" columnGap={10} UNSAFE_style={{ width: '100%' }}>
                <div className="deckPaneCardMain">
                    <Flex direction="row" alignItems="center" columnGap={10}>
                        <CardTypeIcon types={types} />
                        {item.count != null && (
                            <Text UNSAFE_className="deckPaneQuantity">{item.count}</Text>
                        )}
                        <span
                            className="cardStatName"
                            onClick={imageUri ? handleSelect : undefined}
                            style={{
                                cursor: imageUri ? 'pointer' : 'default',
                                color: item.isFrontFace === false ? '#fff49a' : '#ffffff',
                            }}
                            title={imageUri ? `Click to view ${item.name}` : item.name}
                        >
                            {item.name}
                        </span>
                    </Flex>
                    {item.extraData && (
                        <div className="deckPaneExtraData">
                            <img
                                src="/resources/nestedItem.png"
                                alt=""
                                className="deckPaneExtraDataIcon"
                            />
                            <span className="deckPaneExtraDataText">{item.extraData}</span>
                        </div>
                    )}
                </div>
                <DeckPaneRowMeta
                    item={item}
                    showScore={showScore}
                    showLinkOut={showLinkOut}
                    onOpenComboDetails={onOpenComboDetails}
                />
            </Flex>
        </div>
    );
}

/**
 * Combo row — mirrors the Commander `.comboRow` layout: left column of
 * horizontal card chips (thumbnail + name), optional wincon/category
 * tags above, right-aligned meta (score/bonus/link-out).
 *
 * Combo items from `CombosPane` already carry the full card object in
 * `item.cards`, so we use each member's own `imageUri` / `types` rather
 * than looking up via `deck.cards`.
 */
function DeckPaneComboRow({ combo, showScore, showLinkOut, onSelectCard, onOpenComboDetails }) {
    const comboData = combo?.comboData || {};
    const isWincon = comboData?.type === 'wincon' || combo?.comboType === 'WINCON';
    const rawCategories = Array.isArray(combo?.categories)
        ? combo.categories
        : Array.isArray(comboData?.categories)
          ? comboData.categories
          : [];
    const tagCategories = rawCategories.filter(Boolean);

    const sortedCards = (combo?.cards || [])
        .filter(Boolean)
        .slice()
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));

    return (
        <div className={`comboRow ${isWincon ? 'is-wincon' : ''}`}>
            <div className="comboRowCards">
                {(isWincon || tagCategories.length > 0) && (
                    <div className="comboRowTags">
                        {isWincon && <span className="comboTypeBadge">Wincon</span>}
                        {tagCategories.map((cat) => (
                            <span key={cat} className="comboCategoryChip">
                                {getPrettyCategoryLabel(cat)}
                            </span>
                        ))}
                    </div>
                )}
                {sortedCards.map((card, idx) => (
                    <ComboCardChip
                        key={`${combo.id}_${card?.id || idx}`}
                        card={card}
                        onSelectCard={onSelectCard}
                    />
                ))}
            </div>
            <div className="comboRowMeta">
                <DeckPaneRowMeta
                    item={combo}
                    showScore={showScore}
                    showLinkOut={showLinkOut}
                    onOpenComboDetails={onOpenComboDetails}
                />
            </div>
        </div>
    );
}

function ComboCardChip({ card, onSelectCard }) {
    const name = card?.name || card?.id || 'Unknown';
    const imageUri = card?.imageUri;
    const handleClick = () => {
        if (imageUri && onSelectCard) onSelectCard(card);
    };
    return (
        <span
            className="comboCardChip"
            onClick={handleClick}
            style={{ cursor: imageUri ? 'pointer' : 'default' }}
            title={imageUri ? `Click to view ${name}` : name}
        >
            {imageUri ? (
                <img src={imageUri} alt={name} className="comboCardThumb" loading="lazy" />
            ) : (
                <span className="comboCardThumbPlaceholder" />
            )}
            <span
                className="comboCardName"
                style={{ color: card?.isFrontFace === false ? '#fff49a' : '#ffffff' }}
            >
                {name}
            </span>
        </span>
    );
}

function DeckPaneRowMeta({ item, showScore, showLinkOut, onOpenComboDetails }) {
    const manabaseBonus = parseFloat(item?.manabaseBonus);
    const synergyBonus = parseFloat(item?.bonus);
    const hasManabaseBonus = Number.isFinite(manabaseBonus) && manabaseBonus > 0;
    const hasSynergyBonus = Number.isFinite(synergyBonus) && synergyBonus > 0;
    const score = parseFloat(item?.score);

    const showSpellbookLink = showLinkOut && item?.href;
    const showComboDetails = item?.type === 'combo' && item?.comboData && onOpenComboDetails;

    return (
        <Flex direction="row" alignItems="center" columnGap={10}>
            {hasManabaseBonus && (
                <Text UNSAFE_className="deckPaneBonus deckPaneBonusManabase">
                    {`+${manabaseBonus.toFixed(1)}`}
                </Text>
            )}
            {hasSynergyBonus && (
                <Text UNSAFE_className="deckPaneBonus deckPaneBonusSynergy">
                    {`+${synergyBonus.toFixed(1)}`}
                </Text>
            )}
            {showScore && Number.isFinite(score) && (
                <Text UNSAFE_className="deckPaneScore">{score.toFixed(1)}</Text>
            )}
            {showSpellbookLink && (
                <ActionButton
                    onPress={() => window.open(item.href, '_blank', 'noopener')}
                    UNSAFE_style={{ width: 28, height: 28 }}
                    data-role="icon-only"
                    aria-label="Open on Commander Spellbook"
                >
                    <LinkOut />
                </ActionButton>
            )}
            {!showSpellbookLink && showComboDetails && (
                <ActionButton
                    onPress={() => onOpenComboDetails(item)}
                    UNSAFE_style={{ width: 28, height: 28 }}
                    data-role="icon-only"
                    aria-label="View combo details"
                >
                    <LinkOut />
                </ActionButton>
            )}
        </Flex>
    );
}
